import { sanityClient } from '../sanityClient';
import { useQuery } from '@tanstack/react-query';
import {
  articleSearchQuery,
  categoryArticlesQuery,
  helpArticleQuery,
  subCategoriesQuery,
  topLevelCategoriesQuery,
} from './helpCentreQueries';
import type { ArbitraryTypedObject, PortableTextBlock } from '@portabletext/types';
import { ConsumerType } from '../consumer';

interface TopLevelCategoriesQueryProps {
  language: string;
  consumer: ConsumerType;
}

interface Category {
  title: string;
  slug: string;
  id: string;
  publishTo: ConsumerType[];
}

export const useTopLevelCategories = (queryProps: TopLevelCategoriesQueryProps) => {
  return useQuery<Category[]>({
    queryKey: ['topLevelCategories', queryProps],
    queryFn: () => sanityClient.fetch(topLevelCategoriesQuery, queryProps),
    suspense: false,
  });
};

interface SubCategoriesQueryProps {
  language: string;
  categoryId: string;
}

export const useSubCategories = (queryProps: SubCategoriesQueryProps) => {
  return useQuery<Category[]>({
    queryKey: ['subCategories', queryProps],
    queryFn: () => sanityClient.fetch(subCategoriesQuery, queryProps),
    suspense: false,
  });
};

interface CategoryArticlesQueryProps {
  language: string;
  categoryId: string;
  consumer: ConsumerType;
}

interface ArticleMinimal {
  title: string;
  slug: string;
}

export const useCategoryArticles = (queryProps: CategoryArticlesQueryProps) => {
  return useQuery<ArticleMinimal[]>({
    queryKey: ['helpArticles', queryProps],
    queryFn: () => sanityClient.fetch(categoryArticlesQuery, queryProps),
    suspense: false,
  });
};

interface HelpArticleQueryProps {
  language: string;
  slug: string;
}

interface Article {
  title: string;
  content: (PortableTextBlock | ArbitraryTypedObject)[];
}

export const useHelpArticle = (queryProps: HelpArticleQueryProps) => {
  return useQuery<Article>({
    queryKey: ['helpArticle', queryProps],
    queryFn: () =>
      sanityClient.fetch(helpArticleQuery, {
        language: queryProps.language,
        slug: queryProps.slug,
      }),
    suspense: false,
  });
};

interface ArticleSearchQueryProps {
  language: string;
  searchText: string;
  consumer: ConsumerType;
}

export const useArticleSearch = (queryProps: ArticleSearchQueryProps) => {
  return useQuery<ArticleMinimal[]>({
    queryKey: ['articleSearch', queryProps],
    queryFn: () => sanityClient.fetch(articleSearchQuery, queryProps),
    suspense: false,
    enabled: !!queryProps.searchText,
  });
};
